
<template>
  <div class="content">
    <h1>Automatically Update Cloudflare DNS A Records with PowerShell (No Extra Dependencies)</h1>
    <h2>Why I Built This Solution</h2>
    <p>
      While building this website, I originally used DuckDNS to update my Cloudflare DNS records dynamically. However, DuckDNS was unreliable and frequently went down. I needed a simple, reliable, and dependency-free way to update my Cloudflare A records when my public IP changed.
    </p>
    <p>
      Since my setup runs on a full Windows environment, alternatives like ddclient required extra dependencies and weren’t straightforward. Instead, I decided to create a simple PowerShell script (PS1) that runs automatically via Task Scheduler whenever my IP changes.
    </p>
    <h2>Why Use PowerShell for This?</h2>
    <p>I will improve this page and provide more info at a later date.</p>
    <ul>
      <li>
        Lightweight & Simple – No need to install additional software or dependencies.
      </li>
      <li>
        Easily Reusable & Portable – The script can be copied anywhere or stored in source control.
      </li>
      <li>
        Direct Control Over API Calls – Unlike external tools, I can modify or extend it as needed.
      </li>
      <li>
        Native Windows Compatibility – Works perfectly with Task Scheduler to trigger updates automatically.
      </li>
    </ul>
    <h2>How It Works</h2>
    <p>This PowerShell script checks the public IP and updates Cloudflare DNS records only when the IP changes.</p>
    <h3>Steps It Performs:</h3>
    <ul>
      <li>Fetches the current public IP (IPv4).</li>
      <li>Checks the last known IP (stored in last_ip.txt).</li>
      <li>If the IP has changed, it:</li>
      <li>
        <ul>
          <li>Retrieves the Cloudflare DNS record IDs for the configured domains.</li>
          <li>Updates the A records on Cloudflare with the new IP.</li>
          <li>Saves the new IP to last_ip.txt to prevent unnecessary updates.</li>
        </ul>
      </li>

      <li>Runs automatically using Windows Task Scheduler whenever the network changes.</li>
    </ul>


    <p>Here is the full code:</p>


    <pre class="cpr-code-block">
    <code class="language-csharp" data-label="Automatically Update Cloudflare DNS A Records with PowerShell" data-prismjs-copy="Copy the powershell snippet">
        # CONFIGURATION (EDIT THESE VALUES)
        $CloudflareAPIKey = ""  # Replace with your Cloudflare API Key
        $CloudflareZoneID = ""  # Replace with your Cloudflare Zone ID (found in Cloudflare Dashboard)
        $DomainsToUpdate = @("subdomain.pittroche.co.uk", "subdomain2.pittroche.co.uk", "subdomain3.pittroche.co.uk")

        # GET PUBLIC IP
        function Get-PublicIP {
          try {
            return (Invoke-WebRequest -Uri "https://ipv4.icanhazip.com" -UseBasicParsing).Content.Trim()
          } catch {
            Write-Host "Failed to get public IPv4 address. Check your internet connection."
            exit 1
          }
        }

        # GET DNS RECORD ID FROM CLOUDFLARE
        function Get-DNSRecordID {
          param ($DomainName)

          $Headers = @{
            "Authorization" = "Bearer $CloudflareAPIKey"
            "Content-Type"  = "application/json"
          }

          $URL = "https://api.cloudflare.com/client/v4/zones/$CloudflareZoneID/dns_records?type=A&name=$DomainName"
          $Response = Invoke-RestMethod -Uri $URL -Headers $Headers -Method Get

          if ($Response.success -eq $true -and $Response.result.Count -gt 0) {
            return $Response.result[0].id
          } else {
            Write-Host "Could not find DNS Record ID for $DomainName"
            return $null
          }
        }

        # UPDATE DNS RECORD
        function Update-DNSRecord {
        param ($DomainName, $RecordID, $NewIP)

          if (-not $RecordID) {
            Write-Host "Skipping update for $DomainName (No Record ID found)"
            return
          }

          $Headers = @{
            "Authorization" = "Bearer $CloudflareAPIKey"
            "Content-Type"  = "application/json"
          }

          $Body = @{
            type    = "A"
            name    = $DomainName
            content = $NewIP
            ttl     = 1
            proxied = $true
          } | ConvertTo-Json -Depth 3

          $URL = "https://api.cloudflare.com/client/v4/zones/$CloudflareZoneID/dns_records/$RecordID"
          $Response = Invoke-RestMethod -Uri $URL -Headers $Headers -Method Put -Body $Body

          if ($Response.success -eq $true) {
            Write-Host "Successfully updated $DomainName to IP $NewIP"
          } else {
            Write-Host "Failed to update $DomainName"
            Write-Host $Response | ConvertTo-Json -Depth 3
          }
        }

        # MAIN EXECUTION
        $PublicIP = Get-PublicIP
        Write-Host "Your Public IP: $PublicIP"

        foreach ($Domain in $DomainsToUpdate) {
          Write-Host "Searching for DNS Record ID of $Domain..."
          $RecordID = Get-DNSRecordID -DomainName $Domain
          if ($RecordID) {
            Write-Host "Found Record ID: $RecordID"
            Write-Host "Updating $Domain..."
            Update-DNSRecord -DomainName $Domain -RecordID $RecordID -NewIP $PublicIP
          }
        }

        Write-Host "DNS Update Complete!"

      </code>
</pre>
    <h2>Setting Up Windows Task Scheduler for Cloudflare DNS Updates</h2>
    <p>To automate this script when the public IP changes, use Windows Task Scheduler.</p>

    <h3>Steps to Create a Task:</h3>
    <div class="steps">
      <ol>
        <li>Open Task Scheduler (<span class="highlight">Win + R</span>, type <span class="highlight">taskschd.msc</span>, and press Enter).</li>
        <li>Click "Create Basic Task...".</li>
        <li>Set a name, e.g., <strong>Update Cloudflare DNS</strong>.</li>
        <li>Select "When a specific event is logged".</li>
        <li>
          Choose:
          <ul>
            <li><strong>Log:</strong> <span class="highlight">Microsoft-Windows-NetworkProfile/Operational</span></li>
            <li><strong>Source:</strong> <span class="highlight">NetworkProfile</span></li>
            <li><strong>Event ID:</strong> <span class="highlight">10000</span> (Triggers when the network changes).</li>
          </ul>
        </li>
        <li>
          Click Next, choose "Start a program", and enter:
          <ul>
            <li><strong>Program/script:</strong> <span class="highlight">powershell.exe</span></li>
            <li><strong>Arguments:</strong> <span class="highlight">-ExecutionPolicy Bypass -File "C:\Domains\tools\update_cloudflare_IP.ps1"</span></li>
          </ul>
        </li>
        <li>Click Finish.</li>
      </ol>
    </div>

    <h2>Conclusion</h2>
    <p>This PowerShell-based Cloudflare IP updater is a simple, effective, and dependency-free way to automate DNS updates on Windows.</p>
    <p>It ensures that Cloudflare always has the correct public IP, eliminating the need for unreliable third-party services like DuckDNS.</p>
    <p>If you're running a self-hosted server on a dynamic IP, this is an easy and efficient solution!</p>
  </div>

</template>

<script>
  import Prism from 'prismjs';
  import "prismjs/components/prism-csharp";
  import '@/assets/prism-vsc-dark-plus.css';
  import "prismjs/plugins/toolbar/prism-toolbar";
  import "prismjs/plugins/copy-to-clipboard/prism-copy-to-clipboard";

  export default {
    data() {
      return {
      };
    },
    mounted() {
      Prism.highlightAll();
      
    }
  };
</script>
